import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Loader2, PartyPopper } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const AGE_RANGES = ["0–13", "14–18", "19–25", "26–45", "Middle Adult", "Senior Adult", "Prefer not to say"];
const MINOR_RANGES = ["0–13", "14–18"];
const VISIT_REASONS = ["Looking for a church home", "New to London", "Invited by family or friend", "Special event", "Seeking spiritual guidance", "Returning to church", "Just visiting", "Other"];
const HEAR_SOURCES = ["Family / Friend", "Website", "YouTube", "Facebook", "Instagram", "Community Event", "Conference", "Online Service", "Christmas at the Fountain", "Men's Breakfast", "Women's Prayer Breakfast", "Community Choir", "Other"];
const SERVE_OPTIONS = ["Prayer", "Phone Call", "Pastoral Visit", "Counselling", "Home Bible Study", "Information about LFCC", "Offering Envelopes", "Volunteer Opportunities", "Other"];
const MINISTRIES = ["Salvation / Becoming a Christian", "Water Baptism in Jesus' Name", "Receiving the Holy Spirit", "New Believers Class", "Church Membership", "Children's Ministry", "Youth Ministry", "Music Ministry", "Outreach Ministry", "Women's Ministry", "Men's Ministry", "Baby Dedication", "Online Services"];
const NEXT_STEPS = ["Learning more about LFCC", "Meeting the Pastor", "Attending New Believers Class", "Volunteering", "Church Membership", "Baptism", "Getting connected to a ministry"];
const CONTACT_METHODS = ["Phone Call", "Text / WhatsApp", "Email"];

const STEPS = ["About You", "Age Group", "Your Visit", "Finding Us", "Ministry & Care", "Stay Connected"];

const initial = {
  name: "", phone: "", email: "", is_first_time: "", visit_reasons: [], age_range: "", gender: "",
  guardian: { name: "", phone: "", email: "" }, heard_about: [], invited_by: "",
  serve_requests: [], ministry_interests: [], next_steps: [],
  contact_permission: "", preferred_contact_methods: [], sms_consent: false, prayer_request: "",
  children_participate: "", children_action: "",
};

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

function Pill({ selected, onClick, label, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`min-h-[48px] px-4 py-2.5 rounded-full border text-sm font-medium text-left transition-[background-color,border-color,color,transform] duration-200 active:scale-[0.97] ${
        selected
          ? "bg-river text-alabaster border-river shadow-md shadow-river/20"
          : "bg-white text-foreground border-border hover:border-water hover:text-river"
      }`}
    >
      <span className="inline-flex items-center gap-2">
        {selected && <Check className="w-3.5 h-3.5" />}
        {label}
      </span>
    </button>
  );
}

function Field({ label, required, children, hint }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold text-river mb-1.5">
        {label} {required && <span className="text-ember">*</span>}
      </span>
      {children}
      {hint && <span className="block text-xs text-muted-foreground mt-1.5">{hint}</span>}
    </label>
  );
}

const inputCls =
  "w-full min-h-[48px] rounded-xl border border-border bg-white px-4 text-base outline-none transition-[border-color,box-shadow] focus:border-water focus:ring-2 focus:ring-water/20";

export default function VisitorForm() {
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k, v) =>
    setForm((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] }));

  const isMinor = MINOR_RANGES.includes(form.age_range);
  const today = new Date().toLocaleDateString("en-CA", { weekday: "long", year: "numeric", month: "long", day: "numeric" });

  const canContinue = () => {
    if (step === 0) return form.name.trim().length > 1;
    if (step === 1) {
      if (!form.age_range) return false;
      if (isMinor) return form.guardian.name.trim().length > 1 && (form.guardian.phone.trim() || form.guardian.email.trim());
      return true;
    }
    if (step === 2) return form.is_first_time !== "";
    if (step === 5) {
      if (form.contact_permission === "") return false;
      if (form.contact_permission === "Yes" && form.preferred_contact_methods.includes("Text / WhatsApp") && !form.sms_consent) return false;
      return true;
    }
    return true;
  };

  const next = () => { setDir(1); setStep((s) => Math.min(s + 1, STEPS.length - 1)); };
  const back = () => { setDir(-1); setStep((s) => Math.max(s - 1, 0)); };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        guardian: isMinor ? form.guardian : null,
        children_interest: form.children_participate === "Yes"
          ? (form.children_action === "register" ? "register" : form.children_action === "info" ? "info" : "")
          : "",
      };
      const { data } = await api.post("/visitors", payload);
      setDone(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="bg-white rounded-3xl border border-border shadow-xl shadow-river/5 p-8 sm:p-12 text-center"
        data-testid="form-success-card"
      >
        <div className="mx-auto w-16 h-16 rounded-full bg-river flex items-center justify-center mb-6">
          <PartyPopper className="w-7 h-7 text-ember-light" />
        </div>
        <p className="text-xs uppercase tracking-[0.25em] font-mono text-ember mb-3">Connection Card Received</p>
        <h3 className="font-serif text-3xl sm:text-4xl text-river mb-4">
          Thank you{form.name ? `, ${form.name.split(" ")[0]}` : ""}!
        </h3>
        <p className="text-muted-foreground max-w-md mx-auto leading-relaxed mb-2">
          {done.returning
            ? "Welcome back — it's wonderful to see you again."
            : "We're so glad you joined us today."}{" "}
          {form.contact_permission === "Yes"
            ? "Someone from our LFCC family will reach out to you soon."
            : "We hope to see you again this Sunday."}
        </p>
        <p className="text-xs font-mono text-muted-foreground mt-6">
          Reference: {done.person_id}
        </p>
        <a
          href={`/survey?ref=${done.person_id}`}
          data-testid="take-survey-link"
          className="inline-flex items-center gap-2 mt-6 min-h-[48px] px-7 rounded-full border-2 border-river text-river text-sm font-semibold hover:bg-river hover:text-alabaster transition-colors"
        >
          Share your experience (1 min)
        </a>
        {form.children_action === "register" && (
          <a
            href="/children"
            data-testid="register-child-link"
            className="inline-flex items-center gap-2 mt-3 min-h-[48px] px-7 rounded-full bg-ember text-white text-sm font-semibold hover:bg-ember-light transition-colors"
          >
            Register Your Child Now →
          </a>
        )}
      </motion.div>
    );
  }

  const variants = {
    enter: (d) => ({ opacity: 0, x: 40 * d }),
    center: { opacity: 1, x: 0 },
    exit: (d) => ({ opacity: 0, x: -40 * d }),
  };

  return (
    <div className="bg-white rounded-3xl border border-border shadow-xl shadow-river/5 overflow-hidden" data-testid="visitor-form">
      <div className="px-6 sm:px-8 pt-6 pb-4 border-b border-border bg-parchment/50">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs uppercase tracking-[0.2em] font-mono text-muted-foreground" data-testid="form-step-indicator">
            Step {step + 1} of {STEPS.length}
          </p>
          <p className="text-xs font-mono text-ember font-medium">{STEPS[step]}</p>
        </div>
        <div className="h-1.5 rounded-full bg-border overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-water to-river rounded-full"
            animate={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            data-testid="form-progress-bar"
          />
        </div>
      </div>

      <div className="p-6 sm:p-8">
        <AnimatePresence mode="wait" custom={dir}>
          <motion.div
            key={step}
            custom={dir}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            {step === 0 && (
              <div className="space-y-5">
                <h3 className="font-serif text-2xl sm:text-3xl text-river">A little about you</h3>
                <Field label="Today's Date">
                  <input data-testid="visit-date-input" className={`${inputCls} bg-parchment/60`} value={today} readOnly />
                </Field>
                <Field label="Your Name" required>
                  <input data-testid="name-input" className={inputCls} placeholder="Full name" value={form.name} onChange={(e) => set("name", e.target.value)} />
                </Field>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Phone / Mobile">
                    <input data-testid="phone-input" type="tel" className={inputCls} placeholder="(519) 555-0123" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                  </Field>
                  <Field label="Email">
                    <input data-testid="email-input" type="email" className={inputCls} placeholder="you@example.com" value={form.email} onChange={(e) => set("email", e.target.value)} />
                  </Field>
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-5">
                <h3 className="font-serif text-2xl sm:text-3xl text-river">Which age group are you in?</h3>
                <div className="flex flex-wrap gap-2.5" data-testid="age-range-group">
                  {AGE_RANGES.map((a) => (
                    <Pill key={a} testid={`age-range-${slug(a)}`} label={a} selected={form.age_range === a} onClick={() => set("age_range", a)} />
                  ))}
                </div>
                <div>
                  <p className="text-sm font-semibold text-river mb-2.5">Gender <span className="font-normal text-muted-foreground text-xs">(optional)</span></p>
                  <div className="flex flex-wrap gap-2.5" data-testid="gender-group">
                    {["Male", "Female"].map((g) => (
                      <Pill key={g} testid={`gender-${g.toLowerCase()}`} label={g} selected={form.gender === g} onClick={() => set("gender", form.gender === g ? "" : g)} />
                    ))}
                  </div>
                </div>
                <AnimatePresence>
                  {isMinor && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ type: "spring", stiffness: 200, damping: 26 }}
                      className="overflow-hidden"
                      data-testid="guardian-section"
                    >
                      <div className="rounded-2xl border border-water/30 bg-water/5 p-5 space-y-4 mt-2">
                        <p className="text-sm font-semibold text-river">Parent / Guardian details</p>
                        <p className="text-xs text-muted-foreground -mt-2">
                          For our younger guests, we connect with a parent or guardian instead.
                        </p>
                        <Field label="Parent / Guardian Name" required>
                          <input data-testid="guardian-name-input" className={inputCls} value={form.guardian.name} onChange={(e) => set("guardian", { ...form.guardian, name: e.target.value })} />
                        </Field>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <Field label="Guardian Phone">
                            <input data-testid="guardian-phone-input" type="tel" className={inputCls} value={form.guardian.phone} onChange={(e) => set("guardian", { ...form.guardian, phone: e.target.value })} />
                          </Field>
                          <Field label="Guardian Email">
                            <input data-testid="guardian-email-input" type="email" className={inputCls} value={form.guardian.email} onChange={(e) => set("guardian", { ...form.guardian, email: e.target.value })} />
                          </Field>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6">
                <div>
                  <h3 className="font-serif text-2xl sm:text-3xl text-river mb-4">Is this your first time at LFCC? <span className="text-ember">*</span></h3>
                  <div className="flex gap-2.5" data-testid="first-time-group">
                    {["Yes", "No"].map((v) => (
                      <Pill key={v} testid={`first-time-${v.toLowerCase()}`} label={v} selected={form.is_first_time === v} onClick={() => set("is_first_time", v)} />
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="font-serif text-2xl sm:text-3xl text-river mb-1">What brings you to LFCC today?</h3>
                  <p className="text-sm text-muted-foreground mb-4">Choose all that apply.</p>
                  <div className="flex flex-wrap gap-2.5" data-testid="visit-reasons-group">
                    {VISIT_REASONS.map((r) => (
                      <Pill key={r} testid={`reason-${slug(r)}`} label={r} selected={form.visit_reasons.includes(r)} onClick={() => toggle("visit_reasons", r)} />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6">
                <div>
                  <h3 className="font-serif text-2xl sm:text-3xl text-river mb-1">How did you hear about LFCC?</h3>
                  <p className="text-sm text-muted-foreground mb-4">Choose all that apply.</p>
                  <div className="flex flex-wrap gap-2.5" data-testid="heard-about-group">
                    {HEAR_SOURCES.map((s) => (
                      <Pill key={s} testid={`source-${slug(s)}`} label={s} selected={form.heard_about.includes(s)} onClick={() => toggle("heard_about", s)} />
                    ))}
                  </div>
                </div>
                <Field label="Were you invited by someone?" hint="We'd love to thank them — every invitation matters.">
                  <input data-testid="invited-by-input" className={inputCls} placeholder="Their name" value={form.invited_by} onChange={(e) => set("invited_by", e.target.value)} />
                </Field>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-6">
                <div>
                  <h3 className="font-serif text-2xl sm:text-3xl text-river mb-1">How can we serve you?</h3>
                  <p className="text-sm text-muted-foreground mb-4">I would appreciate…</p>
                  <div className="flex flex-wrap gap-2.5" data-testid="serve-group">
                    {SERVE_OPTIONS.map((s) => (
                      <Pill key={s} testid={`serve-${slug(s)}`} label={s} selected={form.serve_requests.includes(s)} onClick={() => toggle("serve_requests", s)} />
                    ))}
                  </div>
                  <AnimatePresence>
                    {form.serve_requests.includes("Other") && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden mt-3">
                        <input
                          data-testid="serve-other-input"
                          className={inputCls}
                          placeholder="Please tell us what you had in mind"
                          value={form.serve_other}
                          onChange={(e) => set("serve_other", e.target.value)}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <div>
                  <h3 className="font-serif text-2xl sm:text-3xl text-river mb-1">I'd like information about</h3>
                  <p className="text-sm text-muted-foreground mb-4">Choose all that apply.</p>
                  <div className="flex flex-wrap gap-2.5" data-testid="ministry-interests-group">
                    {MINISTRIES.map((m) => (
                      <Pill key={m} testid={`ministry-${slug(m)}`} label={m} selected={form.ministry_interests.includes(m)} onClick={() => toggle("ministry_interests", m)} />
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="font-serif text-2xl sm:text-3xl text-river mb-4">Do you have a child/children who may participate in LFCC Children's Ministry?</h3>
                  <div className="flex gap-2.5" data-testid="children-participate-group">
                    {["Yes", "No"].map((v) => (
                      <Pill key={v} testid={`children-participate-${v.toLowerCase()}`} label={v} selected={form.children_participate === v} onClick={() => set("children_participate", v)} />
                    ))}
                  </div>
                  <AnimatePresence>
                    {form.children_participate === "Yes" && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden" data-testid="children-action-section">
                        <p className="text-sm font-semibold text-river mt-4 mb-2.5">Would you like to register your child for Children's Ministry?</p>
                        <div className="flex flex-wrap gap-2.5">
                          {[["register", "Yes — Register Now"], ["info", "Maybe — Send Me Information"], ["not_today", "Not Today"]].map(([v, label]) => (
                            <Pill key={v} testid={`children-action-${v}`} label={label} selected={form.children_action === v} onClick={() => set("children_action", v)} />
                          ))}
                        </div>
                        {form.children_action === "register" && (
                          <p className="text-xs text-muted-foreground mt-2.5">After you send your card, you'll get a link to the secure Children's Ministry registration form.</p>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            )}

            {step === 5 && (
              <div className="space-y-6">
                <div>
                  <h3 className="font-serif text-2xl sm:text-3xl text-river mb-1">My next step</h3>
                  <p className="text-sm text-muted-foreground mb-4">I'm interested in…</p>
                  <div className="flex flex-wrap gap-2.5" data-testid="next-steps-group">
                    {NEXT_STEPS.map((n) => (
                      <Pill key={n} testid={`next-${slug(n)}`} label={n} selected={form.next_steps.includes(n)} onClick={() => toggle("next_steps", n)} />
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="font-serif text-2xl sm:text-3xl text-river mb-4">Would you like someone from LFCC to contact you? <span className="text-ember">*</span></h3>
                  <div className="flex gap-2.5" data-testid="contact-permission-group">
                    {["Yes", "No"].map((v) => (
                      <Pill key={v} testid={`contact-permission-${v.toLowerCase()}`} label={v} selected={form.contact_permission === v} onClick={() => set("contact_permission", v)} />
                    ))}
                  </div>
                </div>
                <AnimatePresence>
                  {form.contact_permission === "Yes" && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ type: "spring", stiffness: 200, damping: 26 }}
                      className="overflow-hidden"
                      data-testid="contact-method-section"
                    >
                      <p className="text-sm font-semibold text-river mb-3">How would you prefer to be contacted?</p>
                      <div className="flex flex-wrap gap-2.5" data-testid="contact-methods-group">
                        {CONTACT_METHODS.map((m) => (
                          <Pill key={m} testid={`contact-method-${slug(m)}`} label={m} selected={form.preferred_contact_methods.includes(m)} onClick={() => toggle("preferred_contact_methods", m)} />
                        ))}
                      </div>
                      {form.preferred_contact_methods.includes("Text / WhatsApp") && (
                        <label className="flex items-start gap-3 mt-4 rounded-xl border border-border bg-parchment/50 px-4 py-3.5 cursor-pointer" data-testid="sms-consent-label">
                          <input
                            type="checkbox"
                            data-testid="sms-consent-checkbox"
                            checked={form.sms_consent}
                            onChange={(e) => set("sms_consent", e.target.checked)}
                            className="mt-0.5 w-5 h-5 shrink-0 accent-[#0F2C59]"
                          />
                          <span className="text-xs text-river/80 leading-relaxed">
                            <span className="font-semibold text-river">Yes, text me.</span> I agree to receive SMS messages from Living Fountain Christian Centre (follow-up and event invitations). Message frequency varies; message and data rates may apply. Reply STOP to opt out anytime. Consent is not a condition of attending.
                          </span>
                        </label>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
                <Field label="Prayer request or comments" hint="Shared only with our pastoral care team — always treated with care.">
                  <textarea data-testid="prayer-request-input" rows={4} className={`${inputCls} py-3 resize-none`} placeholder="Optional — we're honoured to pray with you." value={form.prayer_request} onChange={(e) => set("prayer_request", e.target.value)} />
                </Field>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
          <button
            type="button"
            data-testid="form-back-button"
            onClick={back}
            disabled={step === 0}
            className="inline-flex items-center gap-2 min-h-[48px] px-5 rounded-full text-sm font-semibold text-river disabled:opacity-30 hover:bg-parchment transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button
              type="button"
              data-testid="form-continue-button"
              onClick={next}
              disabled={!canContinue()}
              className="inline-flex items-center gap-2 min-h-[48px] px-7 rounded-full bg-river text-alabaster text-sm font-semibold hover:bg-river-light disabled:opacity-40 transition-[background-color,transform] active:scale-[0.97]"
            >
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              data-testid="visitor-form-submit-button"
              onClick={submit}
              disabled={!canContinue() || submitting}
              className="inline-flex items-center gap-2 min-h-[48px] px-7 rounded-full bg-ember text-white text-sm font-semibold hover:bg-ember-light disabled:opacity-40 transition-[background-color,transform] active:scale-[0.97]"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Send My Card
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
